import { useTranslation } from 'react-i18next';

export type SupportedLang = 'fr' | 'en' | 'ar' | 'es' | 'de';

export type LocalizedText = Partial<Record<SupportedLang, string>>;

// Une valeur localisée n'est "remplie" (donc à sauvegarder/envoyer) que si au
// moins une des 5 langues a du texte — le français n'est plus une langue pivot
// obligatoire, un prof peut rédiger son profil uniquement en anglais par exemple.
export function hasLocalizedText(value: Partial<LocalizedText> | null | undefined): boolean {
  if (!value) return false;
  return Object.values(value).some((text) => text?.trim());
}

// Retire les clés de langue vides ("") avant envoi à l'API : le backend valide
// chaque langue présente avec @MinLength(1), donc une clé comme { fr: '' } (ex.
// un champ touché puis effacé dans LocalizedTextInput) ferait échouer toute la
// requête avec 400, même si une autre langue est bien remplie.
export function cleanLocalizedText<T extends Partial<LocalizedText>>(value: T): T {
  const cleaned = { ...value };
  for (const key of Object.keys(cleaned) as (keyof T)[]) {
    if (!(cleaned[key] as unknown as string | undefined)?.trim()) {
      delete cleaned[key];
    }
  }
  return cleaned;
}

export function resolveLocalized(
  value: Partial<LocalizedText> | null | undefined,
  lang: string,
): string {
  if (!value) return '';
  return (
    value[lang as SupportedLang] ||
    value.fr ||
    Object.values(value).find((text) => text?.trim()) ||
    ''
  );
}

export function useLocalizedText() {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'fr';
  return (value: Partial<LocalizedText> | null | undefined) => resolveLocalized(value, lang);
}
